import { useState, useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useSeoFetch } from '@/hooks/useSeoFetch';
import { getActiveOrgId } from '@/lib/activeOrg';
import financePayrollService, {
  BulkProcessor,
  BulkReport,
  parseWarpCSV,
  matchProfilesToRows,
  ParsedPayrollRow,
} from '@/services/financePayroll';
import { FetchApiIds } from '@/types';
import { Paths } from '@/types/routes';
import FinanceXPayrollComponent from './FinanceXPayroll.component';

/** Shape returned by findReportsForPeriod, surfaced in the clash warning. */
export type ExistingReport = Awaited<
  ReturnType<typeof financePayrollService.findReportsForPeriod>
>[number];

const FinanceXPayrollContainer = (): JSX.Element => {
  const orgId = getActiveOrgId();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: processors = [], isLoading: loadingProcessors } = useSeoFetch<BulkProcessor[]>(
    [FetchApiIds.FX_BULK_PROCESSORS, orgId],
    () => financePayrollService.getProcessors(),
    { retry: false }
  );

  const { data: reports = [], isLoading: loadingReports } = useSeoFetch<BulkReport[]>(
    [FetchApiIds.FX_BULK_REPORTS, orgId],
    () => financePayrollService.getReports(),
    { retry: false }
  );

  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [showUpload, setShowUpload] = useState(false);

  /** Reports already covering the chosen period — set when a clash is found. */
  const [periodClash, setPeriodClash] = useState<ExistingReport[] | null>(null);

  const handleUpload = useCallback(
    async (vendorId: string, file: File, period: { start: string; end: string }, force = false) => {
      setUploading(true);
      setUploadError(null);
      try {
        const text = await file.text();
        let rows: ParsedPayrollRow[];

        // Detect format by file content
        const firstLine = text.split('\n')[0] ?? '';
        if (firstLine.includes('Last Name') && firstLine.includes('Gross Total')) {
          rows = parseWarpCSV(text);
        } else {
          // Default to Warp format for now
          rows = parseWarpCSV(text);
        }

        if (rows.length === 0) {
          setUploadError('No valid rows found in CSV file.');
          return;
        }

        // Guard against re-uploading a period that already has a report. This
        // is what let the April journal in three times and June's twice,
        // triple-counting every contractor. `force` is the user overriding it
        // deliberately after seeing what already exists.
        if (!force) {
          const existing = await financePayrollService.findReportsForPeriod(
            period.start,
            period.end
          );
          if (existing.length > 0) {
            setPeriodClash(existing);
            return;
          }
        }

        const matches = await matchProfilesToRows(rows);
        const reportId = await financePayrollService.createReport(
          vendorId,
          rows,
          matches,
          file.name,
          period
        );

        queryClient.invalidateQueries(FetchApiIds.FX_BULK_REPORTS);
        queryClient.invalidateQueries(FetchApiIds.FX_BULK_PROCESSORS);
        setShowUpload(false);
        navigate(Paths.FINANCEX_PAYROLL_REPORT.replace(':reportId', reportId));
      } catch (err: any) {
        setUploadError(err?.message ?? 'Failed to upload report.');
      } finally {
        setUploading(false);
      }
    },
    [queryClient, navigate]
  );

  const handleRowClick = useCallback(
    (report: BulkReport) => {
      navigate(Paths.FINANCEX_PAYROLL_REPORT.replace(':reportId', report.shortId));
    },
    [navigate]
  );

  return (
    <FinanceXPayrollComponent
      processors={processors}
      reports={reports}
      isLoading={loadingProcessors || loadingReports}
      showUpload={showUpload}
      onShowUpload={() => setShowUpload(true)}
      onCloseUpload={() => {
        setShowUpload(false);
        setUploadError(null);
      }}
      uploading={uploading}
      uploadError={uploadError}
      onUpload={handleUpload}
      periodClash={periodClash}
      onDismissClash={() => setPeriodClash(null)}
      onRowClick={handleRowClick}
    />
  );
};

export default FinanceXPayrollContainer;
